/* Epic theme only: grouping rules and mobile drawer presentation. */
(function () {
  'use strict';

  window.ProductSpecs = window.ProductSpecs || {};

  const originalCreateDom = window.ProductSpecs.createDom;

  const EPIC_CONTENT_CLASS_BY_SECTION = Object.freeze({
    description: 'frengee-epic-details-content',
    specifications: 'frengee-epic-specs-content',
    geometry: 'frengee-epic-geometry-content',
    componentGeometry: 'frengee-epic-components-content',
    sizeGuide: 'frengee-epic-size-guide-content',
  });

  const EPIC_HEADER_CLASS_BY_SECTION = Object.freeze({
    description: 'frengee-epic-details-header',
    specifications: 'frengee-epic-specs-header',
    geometry: 'frengee-epic-geometry-header',
    componentGeometry: 'frengee-epic-components-header',
    sizeGuide: 'frengee-epic-size-guide-header',
  });

  const EPIC_SPEC_CATEGORY_TITLES = Object.freeze({
    frameset: 'Frameset',
    groupset: 'Groupset',
    wheelset: 'Wheelset',
    eBikeSystem: 'E-Bike System',
    other: 'Other',
  });

  const EPIC_SPEC_CATEGORY_ORDER = Object.freeze([
    'frameset',
    'groupset',
    'wheelset',
    'eBikeSystem',
    'other',
  ]);

  const EPIC_SPEC_FIELD_GROUPS = Object.freeze({
    frameset: [
      'frame',
      'fork',
      'fenders',
      'handlebars',
      'handlebarType',
      'headlight',
      'headset',
      'innerHandlebarSize',
      'intHandlebarSize',
      'rearShock',
      'seatBinder',
      'seatPost',
      'stem',
      'stemModel',
      'stemSize',
      'handlebarModel',
      'handlebarSize',
      'frameset',
    ],
    groupset: [
      'bottomBracket',
      'brakeLevers',
      'cassette',
      'chain',
      'chainCase',
      'chainring',
      'chainRings',
      'crankLength',
      'crank lenght',
      'crankset',
      'frontBrake',
      'frontBrakeCaliper',
      'frontBrakeDisc',
      'frontBrakePad',
      'frontBrakeRotor',
      'frontBrakeType',
      'frontDerailleur',
      'group',
      'groupset',
      'pedals',
      'rack',
      'rearBrake',
      'rearBrakeCaliper',
      'rearBrakeDisc',
      'rearBrakePad',
      'rearBrakeRotor',
      'rearBrakeType',
      'rearDerailleur',
      'rotors',
      'shiftLevers',
    ],
    wheelset: [
      'approved_tire_widths',
      'design_tire',
      'front_hub_bearing',
      'front_lacing_pattern',
      'front_rim',
      'front_rim_dimensions',
      'front_spokes',
      'front_weight',
      'frontHub',
      'frontTire',
      'frontWheel',
      'hole_count',
      'hubs',
      'innerTubes',
      'insert_compatibility',
      'max_pressure',
      'nipples',
      'rear_freehub_body',
      'rear_hub_bearing',
      'rear_lacing_pattern',
      'rear_rim',
      'rear_rim_dimensions',
      'rear_spokes',
      'rear_weight',
      'rearHub',
      'rearTire',
      'rearWheel',
      'rim_construction',
      'rims',
      'spokes',
      'tire_type',
      'tires',
      'wheel',
      'wheel_size',
      'wheels',
      'wheelset',
    ],
    eBikeSystem: [
      'battery',
      'batteryPack',
      'charger',
      'controller',
      'display',
      'e-bike',
      'e-bike system',
      'eBike',
      'eBikeController',
      'eBikeDisplay',
      'eBikeSystem',
      'electricMotor',
      'motor',
      'taillight',
      'uiRemote',
      'wiringHarness',
    ],
    other: [
      'assembly_method',
      'bell',
      'bikeLock',
      'brake_compatibility',
      'experience',
      'fit',
      'gender',
      'grips',
      'internalGearhub',
      'kickstand',
      'material',
      'other',
      'saddle',
      'swat',
      'tape',
      'technology',
      'ulCertification',
    ],
  });

  const normalizeEpicSpecFieldKey = (value) =>
    String(value || '')
      .replace(/^(frengee:|custom:|:)+/i, '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

  const EPIC_SPEC_CATEGORY_BY_FIELD = (() => {
    const map = new Map();

    Object.entries(EPIC_SPEC_FIELD_GROUPS).forEach(([category, fields]) => {
      fields.forEach((field) => {
        map.set(normalizeEpicSpecFieldKey(field), category);
      });
    });

    return map;
  })();

  const getEpicSpecCategory = (row) =>
    EPIC_SPEC_CATEGORY_BY_FIELD.get(normalizeEpicSpecFieldKey(row?.key)) ||
    EPIC_SPEC_CATEGORY_BY_FIELD.get(normalizeEpicSpecFieldKey(row?.label)) ||
    'other';

  const createEpicSpecItem = (ctx, row) => {
    const label = String(row?.label || row?.key || '').trim();
    const value = String(row?.value || '').trim();

    if (!label || !value) {
      return null;
    }

    const item = document.createElement('div');
    const title = document.createElement('strong');
    const valueNode = document.createElement('p');

    ctx.safe.setText(title, label);
    ctx.safe.setHtml(valueNode, value);

    item.appendChild(title);
    item.appendChild(valueNode);
    return item;
  };

  const setEpicAccordionOpen = (accordion, isOpen) => {
    const body = accordion.querySelector('.frengee-epic-accordion-body');
    const head = accordion.querySelector('.frengee-epic-accordion-head');

    accordion.classList.toggle('frengee-epic-open', isOpen);
    head?.setAttribute('aria-expanded', String(isOpen));

    if (body) {
      body.setAttribute('aria-hidden', String(!isOpen));
    }
  };

  const closeSiblingEpicAccordions = (accordion) => {
    const list = accordion.closest('.frengee-epic-accordion-list');

    list?.querySelectorAll('.frengee-epic-accordion').forEach((candidate) => {
      if (candidate !== accordion) {
        setEpicAccordionOpen(candidate, false);
      }
    });
  };

  const bindEpicAccordionHandlers = (root) => {
    root.querySelectorAll('.frengee-epic-accordion-head').forEach((head) => {
      if (head.dataset.epicAccordionHandlerBound === 'true') {
        return;
      }

      const accordion = head.closest('.frengee-epic-accordion');
      if (!accordion) {
        return;
      }

      const toggle = () => {
        const shouldOpen = !accordion.classList.contains('frengee-epic-open');
        closeSiblingEpicAccordions(accordion);
        setEpicAccordionOpen(accordion, shouldOpen);
      };

      head.dataset.epicAccordionHandlerBound = 'true';
      head.addEventListener('click', toggle);
      head.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle();
        }
      });
    });
  };

  const createEpicAccordion = (ctx, titleText, contentNodes) => {
    const accordion = document.createElement('div');
    accordion.className = 'frengee-epic-accordion';

    const head = document.createElement('div');
    head.className = 'frengee-epic-accordion-head';
    head.setAttribute('role', 'button');
    head.setAttribute('tabindex', '0');
    head.setAttribute('aria-expanded', 'false');

    const title = document.createElement('p');
    ctx.safe.setText(title, titleText);

    const icon = document.createElement('span');
    icon.className = 'frengee-epic-accordion-icon';
    icon.setAttribute('aria-hidden', 'true');
    ctx.safe.setText(icon, '+');

    const body = document.createElement('div');
    body.className = 'frengee-epic-accordion-body';
    body.setAttribute('aria-hidden', 'true');

    const content = document.createElement('div');
    content.className = 'frengee-epic-accordion-body-content';
    contentNodes.forEach((node) => content.appendChild(node));

    body.appendChild(content);
    head.appendChild(title);
    head.appendChild(icon);
    accordion.appendChild(head);
    accordion.appendChild(body);

    return accordion;
  };

  const createEpicSpecsAccordionList = (ctx, rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return null;
    }

    const groupedNodes = EPIC_SPEC_CATEGORY_ORDER.reduce((groups, key) => {
      groups[key] = [];
      return groups;
    }, {});

    rows.forEach((row) => {
      const item = createEpicSpecItem(ctx, row);
      if (!item) {
        return;
      }

      groupedNodes[getEpicSpecCategory(row)].push(item);
    });

    const list = document.createElement('div');
    list.className = 'frengee-epic-accordion-list';

    EPIC_SPEC_CATEGORY_ORDER.forEach((category) => {
      const contentNodes = groupedNodes[category];
      if (contentNodes.length === 0) {
        return;
      }

      list.appendChild(
        createEpicAccordion(
          ctx,
          EPIC_SPEC_CATEGORY_TITLES[category],
          contentNodes,
        ),
      );
    });

    return list.childNodes.length > 0 ? list : null;
  };

  const moveChildNodes = (source, destination) => {
    while (source.firstChild) {
      destination.appendChild(source.firstChild);
    }
  };

  const clearActiveTabs = (ctx) => {
    ctx.wrapper
      ?.querySelectorAll('.frengee-tab-item')
      .forEach((tab) => tab.classList.remove('frengee-active'));
    ctx.wrapper
      ?.querySelectorAll('.frengee-tab-panel')
      .forEach((panel) => panel.classList.remove('frengee-active'));
  };

  const syncEpicTabVisibility = (ctx) => {
    if (!shouldUseEpicTabsExperience(ctx)) {
      return;
    }

    ctx.wrapper?.querySelectorAll('.frengee-tab-panel').forEach((panel) => {
      if (panel.classList.contains('frengee-active')) {
        panel.style.removeProperty('display');
      } else {
        panel.style.setProperty('display', 'none', 'important');
      }
    });
  };

  const bindEpicTabVisibilityHandlers = (ctx) => {
    if (
      !ctx.wrapper ||
      ctx.wrapper.dataset.epicTabVisibilityHandlerBound === 'true'
    ) {
      return;
    }

    ctx.wrapper.dataset.epicTabVisibilityHandlerBound = 'true';
    ctx.wrapper.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest('[data-epic-tab-close]')) clearActiveTabs(ctx);

      if (
        target.closest('.frengee-tab-item') ||
        target.closest('[data-epic-tab-close]')
      ) {
        syncEpicTabVisibility(ctx);
      }
    });
  };

  const shouldUseEpicTabsExperience = (ctx) =>
    Boolean(
      ctx.specsElement?.closest('.epic-specifications-block') &&
      ctx.specsElement?.dataset.displayStyle === 'tabs',
    );

  const syncExperienceMode = (ctx) => {
    const enabled = shouldUseEpicTabsExperience(ctx);

    if (ctx.wrapper) {
      ctx.wrapper.classList.toggle('frengee-epic-specifications-tabs', enabled);
    }

    return enabled;
  };

  const createEpicTabHeader = (ctx, label, sectionKey) => {
    const headerClass = EPIC_HEADER_CLASS_BY_SECTION[sectionKey];
    if (!headerClass) {
      return null;
    }

    const header = document.createElement('div');
    header.className = headerClass;
    header.classList.add('frengee-epic-tab-header');

    const title = document.createElement('h2');
    title.className = 'frengee-epic-tab-title';
    ctx.safe.setText(title, label);

    const closeButton = document.createElement('button');
    closeButton.className = 'frengee-epic-tab-close';
    closeButton.type = 'button';
    closeButton.setAttribute('data-epic-tab-close', 'true');
    closeButton.setAttribute('aria-label', `Close ${label}`);
    ctx.safe.setText(closeButton, 'Close');

    header.appendChild(title);
    header.appendChild(closeButton);
    return header;
  };

  const decorateEpicPanel = (ctx, panel, label, section = null) => {
    if (!panel || panel.dataset.epicDecorated === 'true') {
      return;
    }

    const sectionKey = panel.getAttribute('data-section-key') || '';
    const contentClass = EPIC_CONTENT_CLASS_BY_SECTION[sectionKey];
    if (!contentClass) {
      return;
    }

    const existingChildren = Array.from(panel.childNodes);
    panel.textContent = '';

    const header = createEpicTabHeader(ctx, label, sectionKey);
    const contentWrapper = document.createElement('div');
    contentWrapper.className = contentClass;

    if (
      sectionKey === 'specifications' &&
      section?.renderKind === 'specifications_table'
    ) {
      const accordionList = createEpicSpecsAccordionList(
        ctx,
        section.content?.rows,
      );

      if (accordionList) {
        contentWrapper.appendChild(accordionList);
      } else {
        existingChildren.forEach((node) => contentWrapper.appendChild(node));
      }
    } else if (
      sectionKey === 'geometry' ||
      sectionKey === 'componentGeometry'
    ) {
      if (sectionKey === 'geometry' && ctx.specsElement?.closest('.epic-specifications-block')?.dataset.geometryImageUrl) {
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'frengee-epic-content-image';

        const image = document.createElement('img');
        image.src = ctx.specsElement?.closest('.epic-specifications-block')?.dataset.geometryImageUrl;
        image.alt = label || 'Geometry';
        image.loading = 'lazy';

        imageWrapper.appendChild(image);
        contentWrapper.appendChild(imageWrapper);
      }

      const tableWrapper = document.createElement('div');
      tableWrapper.className = 'frengee-epic-content-table';
      existingChildren.forEach((node) => tableWrapper.appendChild(node));
      contentWrapper.appendChild(tableWrapper);
    } else if (sectionKey === 'sizeGuide') {
      const firstElement =
        existingChildren.length === 1 && existingChildren[0]?.nodeType === 1
          ? existingChildren[0]
          : null;

      if (
        firstElement &&
        firstElement.classList?.contains('frengee-section-image-wrapper')
      ) {
        firstElement.className = 'frengee-epic-size-guide-image-wrapper';
        const image = firstElement.querySelector('img');
        if (image) {
          image.className = 'frengee-epic-size-guide-image';
        }
        contentWrapper.appendChild(firstElement);
      } else {
        existingChildren.forEach((node) => contentWrapper.appendChild(node));
      }
    } else {
      existingChildren.forEach((node) => contentWrapper.appendChild(node));
    }

    if (header) {
      panel.appendChild(header);
    }
    panel.appendChild(contentWrapper);
    bindEpicAccordionHandlers(panel);
    panel.dataset.epicDecorated = 'true';
  };

  const applyEpicTabsDom = (ctx, sections = []) => {
    if (!syncExperienceMode(ctx)) {
      return;
    }

    const tabsList = ctx.wrapper?.querySelector(
      '.frengee-product-tabs .frengee-tabs-list',
    );
    const tabsContent = ctx.wrapper?.querySelector(
      '.frengee-product-tabs .frengee-tabs-content',
    );

    if (!tabsList || !tabsContent) {
      return;
    }

    Array.from(tabsContent.querySelectorAll('.frengee-tab-panel')).forEach(
      (panel) => {
        const matchingTab = tabsList.querySelector(
          `.frengee-tab-item[data-tab="${panel.id}"]`,
        );
        const label = matchingTab?.textContent?.trim() || '';

        if (matchingTab) {
          matchingTab.setAttribute(
            'data-section-key',
            panel.getAttribute('data-section-key') || '',
          );
        }

        const sectionKey = panel.getAttribute('data-section-key') || '';
        const section =
          sections.find((candidate) => candidate?.key === sectionKey) ||
          null;

        decorateEpicPanel(ctx, panel, label, section);
      },
    );

    if (window.innerWidth <= 749) {
      clearActiveTabs(ctx);
    }

    bindEpicTabVisibilityHandlers(ctx);
    syncEpicTabVisibility(ctx);
  };

  // Expose theme-owned grouping to the presentation adapter even while the
  // installed app still provides the legacy grouping wrapper.
  window.ProductSpecs.epicThemeGroups = {
    getCategory: getEpicSpecCategory,
    titles: EPIC_SPEC_CATEGORY_TITLES,
  };
  if (
    typeof originalCreateDom !== 'function' ||
    originalCreateDom.epicThemeGrouping === true ||
    originalCreateDom.__epicCompatibilityWrapped === true
  ) return;

  window.ProductSpecs.createDom = function (ctx) {
    const dom = originalCreateDom(ctx);
    if (!ctx.specsElement?.closest('.epic-specifications-block')) return dom;
    const originalCreateTabsView = dom.createTabsView;
    const originalShowContent = dom.showContent;

    dom.syncExperienceMode = () => {
      syncExperienceMode(ctx);
    };

    dom.createTabsView = (sections, currentVariantId = null) => {
      originalCreateTabsView(sections, currentVariantId);
      applyEpicTabsDom(ctx, sections);
    };

    dom.showContent = () => {
      syncExperienceMode(ctx);
      originalShowContent();
    };

    return dom;
  };

  window.ProductSpecs.createDom.epicThemeGrouping = true;
})();
